import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@progress/kendo-react-indicators';
import { isNull } from '@ricoh/ria.client.core/utils/data/object';
import classNames from 'classnames';

import type { LoaderWrapperComponent } from './wrapper.type';

const LoaderWrapper: LoaderWrapperComponent = function ({
  type = 'pulsing',
  show = false,
  loadingText,
  opacity = 0.75,
  zIndex = 3,
  size = 'medium',
  position = 'absolute',
  anchor = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  align = {
    vertical: 'center',
    horizontal: 'center',
  },
  className,
  containerClassName,
  style,
  containerStyle,
  children,
}) {
  const { t } = useTranslation();

  const isCustomContainerZIndex = useMemo<boolean>(
    () =>
      classNames(containerClassName)
        .split(' ')
        .some((item) => item.trim().includes('z-')),
    [containerClassName],
  );

  return (
    <div
      className={classNames(
        `position-relative`,
        {
          'z-0': isNull(containerStyle?.zIndex) && !isCustomContainerZIndex,
          'pointer-event-none': show,
        },
        containerClassName,
      )}
      style={containerStyle}
    >
      {show && (
        <>
          <div
            className={`bg-white top-0 bottom-0 start-0 end-0 position-${position}`}
            style={{ opacity, zIndex }}
          />

          <div
            className={classNames(
              `d-flex position-${position} gap-1`,
              {
                'align-items-start': align.vertical === 'top',
                'align-items-center': align.vertical === 'center',
                'align-items-end': align.vertical === 'bottom',

                'justify-content-start': align.horizontal === 'left',
                'justify-content-center': align.horizontal === 'center',
                'justify-content-end': align.horizontal === 'right',
              },
              className,
            )}
            style={{
              zIndex: zIndex + 1,
              ...anchor,
              ...style,
            }}
          >
            <Loader
              type={type}
              size={size}
            />
            {loadingText &&
              (() => {
                if (loadingText === true) {
                  return t(`loading_dot`);
                }

                return loadingText;
              })()}
          </div>
        </>
      )}

      {children}
    </div>
  );
};

export default LoaderWrapper;
